import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Container,
  Typography,
  Button,
  Divider,
  Paper,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AttachmentIcon from '@mui/icons-material/Attachment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import PageLoader from '../../Components/Spinner/PageLoader';
import { HttpClient } from '../../rest/utils/http';
import { imgUrl } from '../../Helpers/utils/Constant';
import { useDashboardNoticeQuery } from '../../Components/Dashboard/api/dashboardEndPoint';

const SingleNotice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [activeAttachmentUrl, setActiveAttachmentUrl] = useState<string>('');
  const [activeAttachmentName, setActiveAttachmentName] = useState<string>('');
  const { allNotice, isLoading: allNoticeLoading } = useDashboardNoticeQuery();

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const loadNotice = async () => {
      // 1. Try single announcement/notice endpoint
      try {
        const res: any = await HttpClient.get(
          `/api/v1/admin/announcement/${id}`,
        );
        if (res?.data && isMounted) {
          setNotice(res.data);
          setLoading(false);
          return;
        }
      } catch (err) {
        // fallback
      }

      try {
        const res: any = await HttpClient.get(`/api/member/notice/${id}`);
        if (res?.data && isMounted) {
          setNotice(res.data);
          setLoading(false);
          return;
        }
      } catch (err) {
        // fallback
      }

      // 2. Search in allNotice list from query
      if (allNotice && allNotice.length > 0) {
        const found = allNotice.find(
          (n: any) =>
            String(n.member_notice_id) === String(id) ||
            String(n.id) === String(id),
        );
        if (found && isMounted) {
          setNotice(found);
          setLoading(false);
          return;
        }
      }

      if (isMounted) {
        setLoading(false);
      }
    };

    loadNotice();

    return () => {
      isMounted = false;
    };
  }, [id, allNotice]);

  const title =
    notice?.title || notice?.member_notice_title || 'Notice Details';

  const content = notice?.content || notice?.member_notice_content || '';

  const date =
    notice?.published_date ||
    notice?.member_notice_created_at ||
    notice?.created_at;

  const getFileUrl = (filePath: string) => {
    if (!filePath) return '';
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    return `${imgUrl}/${filePath}`;
  };

  const thumbnailUrl = notice?.thumbnail ? getFileUrl(notice.thumbnail) : null;

  // Extract attachment document URLs
  const attachmentFiles: string[] = [];

  if (notice?.member_notice_file) {
    attachmentFiles.push(notice.member_notice_file);
  }

  if (Array.isArray(notice?.attachments) && notice.attachments.length > 0) {
    notice.attachments.forEach((att: any) => {
      const urlStr = typeof att === 'string' ? att : att?.url;
      if (urlStr && !attachmentFiles.includes(urlStr)) {
        attachmentFiles.push(urlStr);
      }
    });
  }

  if (attachmentFiles.length === 0 && notice?.file) {
    attachmentFiles.push(notice.file);
  }

  const handleOpenAttachmentModal = (path: string, name: string) => {
    setActiveAttachmentUrl(getFileUrl(path));
    setActiveAttachmentName(name);
    setOpenModal(true);
  };

  return (
    <Container maxWidth='lg' sx={{ pt: 6 }}>
      {/* Top Navigation */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/notice')}
          variant='outlined'
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Back to Notices
        </Button>

        {date && (
          <Chip
            icon={<CalendarTodayIcon style={{ fontSize: 16 }} />}
            label={`Published: ${moment(date).format('MMMM Do YYYY, h:mm a')}`}
            variant='outlined'
            size='medium'
            sx={{ fontWeight: 500 }}
          />
        )}
      </Box>

      {loading || allNoticeLoading ? (
        <PageLoader />
      ) : notice ? (
        <Card
          sx={{
            borderRadius: '16px',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
          }}
        >
          {/* Blurred Thumbnail Header Banner with Title */}
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 220, sm: 280, md: 320 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              bgcolor: 'primary.dark',
              p: { xs: 3, sm: 4, md: 5 },
            }}
          >
            {/* Blurred Background Image */}
            {thumbnailUrl ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${thumbnailUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'blur(8px)',
                  transform: 'scale(1.08)',
                  zIndex: 1,
                }}
              />
            ) : null}

            {/* Dark Gradient Overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: thumbnailUrl
                  ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.45) 0%, rgba(15, 23, 42, 0.85) 100%)'
                  : 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                zIndex: 2,
              }}
            />

            {/* Banner Foreground Content: Title on top of Thumbnail */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 3,
                textAlign: 'center',
                color: '#ffffff',
                maxWidth: '900px',
                mx: 'auto',
              }}
            >
              <Typography
                variant='overline'
                sx={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  letterSpacing: '1.5px',
                  display: 'block',
                  mb: 1,
                }}
              >
                ATAB OFFICIAL NOTICE
              </Typography>

              <Typography
                variant='h3'
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.35rem', sm: '1.75rem', md: '2.1rem' },
                  lineHeight: 1.35,
                  color: '#ffffff',
                  textShadow: '0 3px 12px rgba(0, 0, 0, 0.5)',
                  mb: 1.5,
                }}
              >
                {title}
              </Typography>
            </Box>
          </Box>

          {/* Notice Content / Description */}
          {content && (
            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 1,
                  fontSize: '1.1rem',
                }}
              >
                Notice Description
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  color: 'text.secondary',
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {content}
              </Typography>
            </Box>
          )}

          <Divider sx={{ mx: { xs: 3, md: 4 } }} />

          {/* Attachments List */}
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
              sx={{ mb: 3 }}
            >
              <AttachmentIcon color='primary' />
              <Typography
                variant='h5'
                sx={{ fontWeight: 700, color: 'text.primary' }}
              >
                Notice Attachments ({attachmentFiles.length})
              </Typography>
            </Stack>

            {attachmentFiles.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {attachmentFiles.map((attPath: string, idx: number) => {
                  const fileName =
                    attPath.split('/').pop() || `Attachment #${idx + 1}`;
                  const isPdf = attPath.toLowerCase().endsWith('.pdf');

                  return (
                    <Paper
                      key={idx}
                      elevation={0}
                      onClick={() =>
                        handleOpenAttachmentModal(
                          attPath,
                          attachmentFiles.length > 1
                            ? `Attachment #${idx + 1}: ${fileName}`
                            : fileName,
                        )
                      }
                      sx={{
                        p: 2.5,
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '10px',
                            bgcolor: 'primary.lighter',
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <AttachmentIcon fontSize='medium' />
                        </Box>
                        <Box>
                          <Typography
                            variant='subtitle1'
                            fontWeight={700}
                            color='text.primary'
                          >
                            {attachmentFiles.length > 1
                              ? `Attachment #${idx + 1}: ${fileName}`
                              : fileName}
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            {isPdf ? 'PDF Document' : 'Attachment File'} • Click
                            to view document in modal reader
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        display='flex'
                        gap={1}
                        alignItems='center'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          onClick={() =>
                            handleOpenAttachmentModal(
                              attPath,
                              attachmentFiles.length > 1
                                ? `Attachment #${idx + 1}: ${fileName}`
                                : fileName,
                            )
                          }
                          size='small'
                          variant='contained'
                          startIcon={<VisibilityIcon fontSize='small' />}
                          sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            fontWeight: 600,
                            px: 2,
                          }}
                        >
                          View Document
                        </Button>
                        <Button
                          component='a'
                          href={getFileUrl(attPath)}
                          target='_blank'
                          rel='noopener noreferrer'
                          size='small'
                          variant='outlined'
                          startIcon={<OpenInNewIcon fontSize='small' />}
                          sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                          }}
                        >
                          New Tab
                        </Button>
                        <Button
                          component='a'
                          href={getFileUrl(attPath)}
                          download
                          target='_blank'
                          rel='noopener noreferrer'
                          size='small'
                          variant='outlined'
                          startIcon={<DownloadIcon fontSize='small' />}
                          sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                          }}
                        >
                          Download
                        </Button>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: 'action.hover',
                  borderRadius: '12px',
                  border: '1px dashed',
                  borderColor: 'divider',
                }}
              >
                <Typography color='text.secondary' variant='body1'>
                  No attachment documents available for this notice.
                </Typography>
              </Paper>
            )}
          </Box>
        </Card>
      ) : (
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant='h6' color='text.secondary'>
            Notice details not found.
          </Typography>
        </Card>
      )}

      {/* Modal Dialog for Document Preview */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth='lg'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant='h6' fontWeight={700} color='text.primary'>
            {activeAttachmentName || 'Document Preview'}
          </Typography>

          <Box display='flex' gap={1} alignItems='center'>
            {activeAttachmentUrl && (
              <>
                <Tooltip title='Open in new tab'>
                  <IconButton
                    component='a'
                    href={activeAttachmentUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    size='small'
                    color='primary'
                  >
                    <OpenInNewIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Download'>
                  <IconButton
                    component='a'
                    href={activeAttachmentUrl}
                    download
                    target='_blank'
                    rel='noopener noreferrer'
                    size='small'
                    color='primary'
                  >
                    <DownloadIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <IconButton
              aria-label='close'
              onClick={() => setOpenModal(false)}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ p: 0, height: '80vh', bgcolor: '#1a1a1a' }}
        >
          {activeAttachmentUrl ? (
            <iframe
              src={activeAttachmentUrl}
              title='Attachment Document Viewer'
              width='100%'
              height='100%'
              style={{
                border: 'none',
                display: 'block',
              }}
            />
          ) : (
            <Box textAlign='center' py={5} color='#ffffff'>
              <Typography variant='h6'>No document URL found</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default SingleNotice;
